'use client';

import React, { useRef, useEffect } from 'react';
import { cn } from '~/lib/utils';

// Configuration types
interface TurnstileProps {
  /**
   * Your Cloudflare Turnstile site key
   */
  siteKey: string;

  /**
   * Called when the user completes the challenge or when token expires
   */
  onVerify: (token: string) => void;

  /**
   * Called when the widget errors
   * @optional
   */
  onError?: (error: string) => void;

  /**
   * Called when the widget expires
   * @optional
   */
  onExpire?: () => void;

  /**
   * Theme of the widget
   * @optional
   */
  theme?: 'light' | 'dark' | 'auto';

  /**
   * Size of the widget
   * @optional
   */
  size?: 'normal' | 'compact';

  /**
   * Whether to start in invisible mode
   * @optional
   */
  invisible?: boolean;

  /**
   * Custom HTML class name
   * @optional
   */
  className?: string;

  /**
   * Language of the widget
   * @optional
   */
  language?: string;

  /**
   * Whether to refresh token automatically when it expires
   * @optional
   */
  refreshExpired?: 'auto' | 'manual';
}

export function Turnstile({
  siteKey,
  onVerify,
  onError,
  onExpire,
  theme = 'auto',
  size = 'normal',
  invisible = false,
  language,
  className,
  refreshExpired = 'auto',
}: TurnstileProps) {
  const ref = useRef<HTMLDivElement>(null);
  const widgetId = useRef<string | null>(null);

  // Script loading and initialization
  useEffect(() => {
    // Check if Turnstile script is already loaded
    if (window.turnstile) {
      renderTurnstile();
      return;
    }

    // Load Turnstile script
    const script = document.createElement('script');
    script.src =
      'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    script.async = true;
    script.defer = true;

    script.onload = () => {
      renderTurnstile();
    };

    document.head.appendChild(script);

    return () => {
      // Remove the script when component unmounts
      if (widgetId.current && window.turnstile) {
        window.turnstile.remove(widgetId.current);
      }

      // Remove the script from the DOM
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
    };
  }, [siteKey]);

  // Render the Turnstile widget
  const renderTurnstile = () => {
    if (!ref.current || !window.turnstile) return;

    if (widgetId.current) {
      window.turnstile.remove(widgetId.current);
    }

    widgetId.current = window.turnstile.render(ref.current, {
      sitekey: siteKey,
      callback: (token: string) => {
        onVerify(token);
      },
      'error-callback': () => {
        if (onError) onError('Turnstile encountered an error');
      },
      'expired-callback': () => {
        if (onExpire) onExpire();
      },
      theme,
      size,
      tabindex: 0,
      'response-field': false,
      language,
      'refresh-expired': refreshExpired,
      appearance: invisible ? 'interaction-only' : undefined,
    });
  };

  return <div ref={ref} className={cn('cf-turnstile', className)} />;
}

// Augment Window object with Turnstile types
declare global {
  interface Window {
    turnstile?: {
      render: (container: HTMLElement, params: Record<string, any>) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
      getResponse: (widgetId: string) => string;
    };
  }
}
